/**
 * The statutory line under "Total time off per year" on sign-up's leave step.
 * Pure — checked by `node src/lib/statutoryLine.checks.mjs`.
 *
 * Group A4, 21/10/2026 (mock-up `Claude outputs/signup-statutory-box-mock-v1.html`,
 * option (a) agreed by Rob). It replaces a hard-coded "UK statutory minimum is 28 days
 * including bank holidays" that every company saw — a French one, an Algerian one, and
 * a UK one on a four-day week alike.
 *
 * ⚠️ NO LAW IN HERE. The weeks, the cap and the floor for the company's week all come
 * from `GET /settings/jurisdiction` → `company_floor`, worked out server-side by the same
 * sum as every employee's floor (`services/entitlement.py`). This file only words it
 * and compares it with the total on screen. If you find yourself typing 5.6 or 28 here,
 * stop — see the LeaveCalculator.jsx audit finding in `routers/settings.py`.
 *
 * ⚠️ ADVICE, NEVER A BLOCK (D4). `short` changes the colour and the words; nothing
 * anywhere should stop the admin pressing Next because of it.
 */

/** 28 -> "28", 22.4 -> "22.4", 4.999 -> "5". */
export const fmt = (n) => String(Math.round(Number(n) * 10) / 10);

/** "UK" for Britain (what people say); otherwise the rule pack's own name. */
export const shortName = (jurisdiction) =>
  (jurisdiction?.country_code || '').toUpperCase() === 'GB' || jurisdiction?.jurisdiction === 'United Kingdom'
    ? 'UK'
    : (jurisdiction?.jurisdiction || '');

/**
 * What to show, or null to show nothing at all.
 *
 *   jurisdiction  — the body of GET /settings/jurisdiction
 *   allowance     — the default annual allowance being typed (days)
 *   bankHolidays  — the company's bank-holiday count on screen
 *   included      — the Included / On top radio
 *
 * Returns { state: 'meets' | 'short', name, working, shortBy, sourceUrl }.
 */
export function statutoryLine({ jurisdiction, allowance, bankHolidays, included }) {
  const floor = jurisdiction?.company_floor;
  if (!jurisdiction?.jurisdiction || !floor || floor.statutory_days == null) return null;

  const a = Number(allowance) || 0;
  const bh = Number(bankHolidays) || 0;
  // What counts towards the minimum. Where the law lets bank holidays count (the UK),
  // that is the whole of the time off; where it doesn't, only the allowance does —
  // and an "included" allowance has the bank holidays taken out of it first.
  const mayInclude = jurisdiction.public_holidays_may_be_included !== false;
  const total = included ? a : a + bh;
  const counts = mayInclude ? total : (included ? Math.max(0, a - bh) : a);

  const days = (n) => `${fmt(n)} ${Number(fmt(n)) === 1 ? 'day' : 'days'}`;
  const week = `${fmt(floor.days_a_week)} ${Number(fmt(floor.days_a_week)) === 1 ? 'day' : 'days'} a week`;
  const working = floor.capped
    ? `${week} × ${fmt(floor.weeks)} weeks = ${fmt(floor.uncapped_days)}, capped at ${days(floor.statutory_days)}`
    : `${week} × ${fmt(floor.weeks)} weeks = ${days(floor.statutory_days)}`;

  const gap = Math.round((floor.statutory_days - counts) * 10) / 10;
  return {
    state: gap > 0 ? 'short' : 'meets',
    name: shortName(jurisdiction),
    working,
    bankHolidaysCount: mayInclude,
    shortBy: gap > 0 ? days(gap) : null,
    sourceUrl: jurisdiction.annual_leave?.source_url || null,
  };
}
